import api, { UserCredentials } from "@/services/api.service";
import { SsoProviderSettings, LoginStatus, LoginPreferences } from "@/store/types";
import store from "@/store/";

/**
 * Manages the routing logic used when SSO is enabled.
 * If the User has come from the SSO login page it will attempt to sign the user in.
 * Otherwise if SSO auto redirect is enabled it will send the user to the SSO login page.
 * In any other case it will return null.
 */
export async function handleSsoSignIn(windowLocation: string): Promise<string> {

	// if specific loginNoRedirect route is specified, bypass redirect
	if (windowLocation.includes("loginNoRedirect")) {
		return null;
	}

	let loginPreferences: LoginPreferences = store.getters.loginPreferences;

	// ssoProviderSettings.ssoEnabled is set to null by default in the store,
	// this will get updated with the login preferences
	if (!loginPreferences ||
		!loginPreferences.ssoProviderSettings ||
		!loginPreferences.ssoProviderSettings.ssoEnabled) {
		try {
			await store.dispatch("loadLoginPreferences");
		} catch (err) {
			console.error("unable to load login preferences");
		}
	}

	const ssoProviderSettings : SsoProviderSettings= store.getters.loginPreferences.ssoProviderSettings;

	// ensure feature is enabled
	if (ssoProviderSettings && ssoProviderSettings.ssoEnabled) {

		// check the redirect route to see if we come from the SSO page
		if (ssoProviderSettings.providerType === ssoProvider.OpenId && windowLocation.includes("id_token")) {

			// harcoded parse as whats returned cant be parsed by URLSearchParams,
			// will need too make it more generic should we use other providers.
			let token = windowLocation.split("=")[1];
			if (token.includes("&")) {
				token = token.split("&")[0];
			}

			const userCredentials: UserCredentials = {
				username: "",
				password: "",
				IdToken: token
			};

			let loginResult = await api.login(userCredentials);

			if (loginResult.data.status == LoginStatus.Success) {
				return "/event-queue";
			} else {
				// Return to login page,
				// by returning null and letting the the navigation guard send us there.
				let error = "Sign on Login Error";
				if (loginResult.data.error) {
					error = `${error} : ${loginResult.data.error}`;
				} else if (loginResult.data.status == LoginStatus.DisabledUser) {
					error = `${error} : Your account has been disabled.`;
				}

				console.error(error);
				return null;
			}
		}
		// We did not just come from our SSO login page and auto redirect is enabled so send the user there.
		else if (ssoProviderSettings.authorizationEndpoint != null && ssoProviderSettings.ssoAutoRedirect) {

			const ssoURL = await getSsoURL(ssoProviderSettings);
			if (ssoProviderSettings.providerType === ssoProvider.OpenId) {
				window.location.assign(ssoURL);
			}

		} else {
			console.log("Redirect not enabled");
			return null;
		}
	}
	return null;
}

/**
 * Retrieves the url of the SSO sign in page along with the parameters needed to make a valid sign in transaction.
 */
export async function getSsoURL(ssoProviderSettings: SsoProviderSettings): Promise<string> {

	if (ssoProviderSettings.ssoEnabled) {
		if (ssoProviderSettings.providerType === ssoProvider.OpenId) {

			// default the redirect url to the current page
			let redirectUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;

			// update the redirect url if an override has been provided
			if (ssoProviderSettings.redirectUrl && ssoProviderSettings.redirectUrl.length > 0) {
				redirectUrl = ssoProviderSettings.redirectUrl;
			}

			return `${ssoProviderSettings.authorizationEndpoint}&redirect_uri=${redirectUrl}&nonce=${genNonce()}`;
		} else if (ssoProviderSettings.providerType === ssoProvider.Okta) {
			return null;
		}
	} else {
		throw new Error("Cannot provide SSO Redirect URL as Single Sign On is not enabled");
	}
}

/**
 * Enum indicating which SSO provider is being used
 */
export enum ssoProvider {
	None = 0,
	OpenId = 1,
	Okta = 2
}

function genNonce() {
	const charset = "0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._~";
	const result = [];
	window.crypto.getRandomValues(new Uint8Array(32)).forEach(c => result.push(charset[c % charset.length]));
	return result.join("");
}
